import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { format, addDays } from "date-fns";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { PMDatePicker } from "../../../utils/PMDatePicker";
import { performDeviationWork } from "../../../../api/quality/deviation";

const ACCENT = "#1463C7";

const dateOptionClass = (active) =>
  `flex-1 rounded-[10px] border-[1.5px] px-3 py-2.5 text-[13px] font-semibold transition-colors ${
    active
      ? "border-transparent text-white shadow-[0_8px_18px_-8px_rgba(20,99,199,.55)]"
      : "border-[#E2E8F0] bg-white text-[#334155] hover:border-[#94A3B8]"
  }`;

/**
 * Minimal "Utfør arbeid" dialog for ordinary users:
 * today / tomorrow / selected date → create + link work order.
 */
export function DeviationPerformWorkDialog({
  open,
  onClose,
  deviation,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [dateOption, setDateOption] = useState("today");
  const [customDate, setCustomDate] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDateOption("today");
    setCustomDate(format(new Date(), "yyyy-MM-dd"));
    setSubmitting(false);
  }, [open, deviation?.id]);

  const previewDate = useMemo(() => {
    if (dateOption === "tomorrow") {
      return format(addDays(new Date(), 1), "yyyy-MM-dd");
    }
    if (dateOption === "custom") return customDate;
    return format(new Date(), "yyyy-MM-dd");
  }, [dateOption, customDate]);

  const canSubmit =
    !!deviation?.id &&
    !deviation?.work_order?.id &&
    !submitting &&
    (dateOption !== "custom" || !!customDate);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await performDeviationWork(deviation.id, {
        date_option: dateOption === "custom" ? "custom" : dateOption,
        execution_date: dateOption === "custom" ? customDate : undefined,
      });

      if (
        result?.isAxiosError ||
        (result?.errors && result.errors.length) ||
        (result?.response?.status ?? 0) >= 400
      ) {
        const msg = Array.isArray(result?.errors)
          ? result.errors.join(", ")
          : result?.errors ||
            t("deviation.perform_work.error");
        toast.error(typeof msg === "string" ? msg : t("deviation.perform_work.error"));
        setSubmitting(false);
        return;
      }

      toast.success(t("deviation.perform_work.success"));
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(t("deviation.perform_work.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={t("deviation.perform_work.title")}
      size="sm"
    >
      <div className="space-y-4 p-1">
        <p className="text-[13px] leading-relaxed text-[#475569]">
          {t("deviation.perform_work.intro")}
        </p>

        {(deviation?.displayTitle || deviation?.title) && (
          <div className="rounded-[12px] border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t("deviation.perform_work.deviation_label")}
            </p>
            <p className="mt-0.5 text-[14px] font-semibold text-[#0F172A]">
              {deviation.displayTitle || deviation.title}
            </p>
          </div>
        )}

        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
            {t("deviation.perform_work.date_label")}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            {[
              ["today", t("deviation.perform_work.date_today")],
              ["tomorrow", t("deviation.perform_work.date_tomorrow")],
              ["custom", t("deviation.perform_work.date_custom")],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                disabled={submitting}
                onClick={() => setDateOption(id)}
                className={dateOptionClass(dateOption === id)}
                style={
                  dateOption === id
                    ? { background: ACCENT, borderColor: ACCENT }
                    : undefined
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {dateOption === "custom" && (
          <PMDatePicker
            name="execution_date"
            label={t("deviation.perform_work.date_pick")}
            placeholder={t("Select Date")}
            value={customDate}
            onChange={(e) => setCustomDate(e?.target?.value || "")}
            disabled={submitting}
            triggerVariant="design"
            accentColor={ACCENT}
          />
        )}

        <p className="text-[12px] text-[#64748B]">
          {t("deviation.perform_work.date_preview", { date: previewDate })}
        </p>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="rounded-[10px] border border-[#E2E8F0] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#334155] hover:bg-[#F8FAFC]"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#E2E8F0] disabled:text-[#94A3B8]"
            style={
              canSubmit
                ? {
                    background: "linear-gradient(135deg,#1463C7,#2D8BE8)",
                    boxShadow: "0 12px 24px -8px rgba(20,99,199,.55)",
                  }
                : undefined
            }
          >
            <WrenchScrewdriverIcon className="h-4 w-4" />
            {submitting
              ? t("deviation.perform_work.submitting")
              : t("deviation.perform_work.submit")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
